import moment from "moment";
import {DayFilter, InterestRegistrationStatus} from "../../generated/graphql";
import i18n from "../../services/i18n";

// ────────────────────────────────────────────────────────────────────────────
// Interessenten-Listen — geteilte Anzeige-Helfer (Wochentage, Zeitfenster,
// Status). Wochentags-Enum kommt aus dem geteilten `DayFilter` (Werte sind
// bereits deutsche Wochentagsnamen).
// ────────────────────────────────────────────────────────────────────────────

// Reihenfolge Mo–So für Selects und Sortierung.
export const WEEKDAY_ORDER: Array<DayFilter> = [
    DayFilter.Montag,
    DayFilter.Dienstag,
    DayFilter.Mittwoch,
    DayFilter.Donnerstag,
    DayFilter.Freitag,
    DayFilter.Samstag,
    DayFilter.Sonntag,
];

export type TimeSlotLike = {
    weekday: DayFilter;
    startTime?: string | null;
    endTime?: string | null;
    sortOrder?: number | null;
};

// Uhrzeit-Spalten liefert PostgreSQL als "HH:mm:ss" — für die Anzeige auf
// "HH:mm" kürzen. Leerer/undefinierter Wert bleibt leer.
export const formatTime = (time?: string | null): string => {
    if (!time) return "";
    const parts = time.split(":");
    if (parts.length >= 2) return `${parts[0]}:${parts[1]}`;

    return time;
};

// Ein einzelnes Zeitfenster als lesbaren String: "Montag 15:00–16:00" bzw.
// nur "Montag", wenn keine Uhrzeit hinterlegt ist.
export const formatTimeSlot = (slot: TimeSlotLike): string => {
    const day = String(slot.weekday);
    if (slot.startTime && slot.endTime) {
        return `${day} ${formatTime(slot.startTime)}–${formatTime(slot.endTime)}`;
    }

    return day;
};

// Sortiert Zeitfenster nach sortOrder, dann Wochentag, dann Startzeit.
export const sortTimeSlots = <T extends TimeSlotLike>(slots: ReadonlyArray<T>): Array<T> => {
    return [...slots].sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;
        if (orderA !== orderB) return orderA - orderB;
        const dayA = WEEKDAY_ORDER.indexOf(a.weekday);
        const dayB = WEEKDAY_ORDER.indexOf(b.weekday);
        if (dayA !== dayB) return dayA - dayB;

        return (a.startTime ?? "").localeCompare(b.startTime ?? "");
    });
};

// Alle gewählten Zeitfenster einer Bekundung als kommaseparierten Text — oder
// den "Alle Zeiten"-Hinweis, wenn der Kunde keine Einschränkung gewählt hat.
export const formatSelectedTimeSlots = (
    slots: ReadonlyArray<TimeSlotLike>,
    allTimesPossible: boolean,
): string => {
    if (allTimesPossible || slots.length === 0) {
        return i18n.containers.interestLists.InterestLists.allTimes();
    }

    return sortTimeSlots(slots).map(formatTimeSlot).join(", ");
};

export const formatRegistrationDate = (date?: string | null): string => {
    return date ? moment(date).format("DD.MM.YYYY HH:mm") : "";
};

// ponytail: defensiver Guard gegen abweichendes Server-Casing/Whitespace
// (z.B. "New " statt "New") — sonst würde AntD den Rohwert als Phantom-Option
// zeigen. Trimmt + matcht case-insensitiv auf den bekannten Enum-Wert.
export const normalizeStatus = (raw?: string | null): InterestRegistrationStatus => {
    const s = String(raw ?? "").trim().toLowerCase();
    const match = STATUS_ORDER.find((status) => status.toLowerCase() === s);

    return match ?? InterestRegistrationStatus.New;
};

// Übersetztes Status-Label. Fällt auf den Rohwert zurück, falls unbekannt.
export const statusLabel = (status: InterestRegistrationStatus): string => {
    switch (normalizeStatus(status)) {
        case InterestRegistrationStatus.New:
            return i18n.containers.interestLists.InterestLists.statusNew();
        case InterestRegistrationStatus.Contacted:
            return i18n.containers.interestLists.InterestLists.statusContacted();
        case InterestRegistrationStatus.Done:
            return i18n.containers.interestLists.InterestLists.statusDone();
        default:
            return String(status);
    }
};

export const STATUS_ORDER: Array<InterestRegistrationStatus> = [
    InterestRegistrationStatus.New,
    InterestRegistrationStatus.Contacted,
    InterestRegistrationStatus.Done,
];
